/**
 * QR payload parsing for the EVI driver PWA.
 *
 * Field stickers in production today carry the v1 URL format:
 *
 *   https://app.evinitiative.com/charging-station/<locationId>?charger=<chargerIdentity>
 *
 * v2 must keep these stickers working forever — the URL on the
 * physical sticker outlives any platform-side rename. parseEviQr
 * normalises any accepted variant to the canonical v1 form so the
 * URL bar post-scan matches what the driver scanned (copy-and-share
 * gives the same URL the recipient could scan again).
 *
 * System camera apps on iOS and Android open these as deep-links
 * into the installed PWA (or fall back to the browser). Spoofability
 * is mitigated by the server-side wallet gate + charger:command
 * permission check — a forged URL can't start a session it wouldn't
 * have been able to start through the regular UI.
 *
 * If the driver arrives via `/scan` with a raw QR reader, the string
 * is fed through `parseEviQr` to normalise it into a route we can
 * navigate to. Anything outside the allowlist → `unknown` so the
 * Scan page renders "Unrecognised code" instead of silently leading
 * the driver somewhere wrong.
 */

/**
 * Hosts recognised as legitimate EVI deep-links. This is a SECURITY GATE:
 * a scanned URL whose host isn't here returns `unknown` rather than
 * navigating, so only trusted EVI hosts are honoured.
 *
 *   - `app.evinitiative.com` — prod, ALWAYS allowed: real field stickers
 *     point at prod regardless of which build scans them.
 *   - the current runtime host (`window.location.hostname`) — so a staging
 *     sticker (`app.stg.evinitiative.com`) scanned by the real reader on
 *     the staging build resolves instead of returning unknown. The web
 *     StaticSite doesn't set VITE_PUBLIC_PWA_HOST, so the actual host is
 *     the reliable source; VITE_PUBLIC_PWA_HOST is also honoured when
 *     present (admin builds set it).
 *   - `localhost` — the dev scan-simulator round-trip.
 *
 * (`drive.evinitiative.com` and the root domain were dropped earlier: the
 * drive subdomain never reached production; the root serves the marketing
 * site, not the PWA.)
 */
import { stickerCharger, stickerStation } from './stickerUrl';

const EVI_QR_ALLOWED_HOSTS: ReadonlyArray<string> = (() => {
  const hosts = new Set<string>(['app.evinitiative.com', 'localhost']);
  const buildHost = import.meta.env['VITE_PUBLIC_PWA_HOST'] as string | undefined;
  if (buildHost) hosts.add(buildHost);
  if (typeof window !== 'undefined' && window.location?.hostname) {
    hosts.add(window.location.hostname);
  }
  return [...hosts];
})();

export type QrParseResult =
  | { kind: 'stall'; locationId: string; chargerIdentity: string; path: string }
  | { kind: 'station'; locationId: string; path: string }
  | { kind: 'unknown' };

/**
 * Parse a QR payload string.
 *
 * Accepts:
 *   - `https://app.evinitiative.com/charging-station/<loc>?charger=<charger>`
 *     (canonical v1 sticker format — what production hardware carries)
 *   - `https://app.evinitiative.com/stations/<loc>/stall/<charger>`
 *     (v2 internal-navigation format — preserved for any deep links
 *     already in the wild)
 *   - `https://app.evinitiative.com/stations/<loc>` (bare station,
 *     opens StationDetailPage so the driver can pick a stall)
 *   - raw paths of the same shapes (in-app demo scan)
 *
 * Returns `{ kind: 'stall' | 'station', ... }` on a match with the
 * normalised path the router can push to. The `path` for a stall
 * match is ALWAYS the canonical `/charging-station/:loc?charger=:c`
 * form — URL bar post-scan reflects the sticker format regardless
 * of which input variant was scanned. Bare `/stations/:loc` keeps
 * its original shape since there's no canonical v1 equivalent for
 * the station-list-only screen.
 */
export function parseEviQr(payload: string): QrParseResult {
  const trimmed = payload.trim();
  if (!trimmed) return { kind: 'unknown' };

  // Accept either a URL or a raw path. The URL parser throws on
  // relative paths, so test the shape first.
  let pathname: string;
  let search: URLSearchParams;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (!EVI_QR_ALLOWED_HOSTS.includes(url.hostname)) {
        return { kind: 'unknown' };
      }
      pathname = url.pathname;
      search = url.searchParams;
    } catch {
      return { kind: 'unknown' };
    }
  } else if (trimmed.startsWith('/')) {
    // Raw path may carry its own query string. Construct a URL with a
    // dummy origin to extract the components consistently.
    try {
      const url = new URL(trimmed, 'https://app.evinitiative.com');
      pathname = url.pathname;
      search = url.searchParams;
    } catch {
      return { kind: 'unknown' };
    }
  } else {
    return { kind: 'unknown' };
  }

  // Match the canonical v1 sticker format FIRST (most permissive
  // hostname allowlist; what production hardware carries today).
  // The `charger` query param carries the OCPP identity; alphanumeric
  // + underscore + hyphen, same alphabet StartTransaction's idTag
  // accepts.
  const stickerLoc = stickerStation(pathname);
  if (stickerLoc) {
    // Same parts the router reads (lib/stickerUrl): the charger in
    // `charger` or one of its aliases.
    const charger = stickerCharger(search);
    const loc = stickerLoc;
    if (charger) {
      return {
        kind: 'stall',
        locationId: loc,
        chargerIdentity: charger,
        // Canonical normalisation — Option B per the URL-standardisation
        // ack. URL bar post-scan reflects the sticker format.
        path: `/charging-station/${loc}?charger=${charger}`,
      };
    }
    // No / invalid `charger` param — degrade gracefully to a bare-
    // station link so the driver lands on the StationDetailPage
    // and can pick a stall manually. Matches App.tsx routing for
    // `/charging-station/<loc>` without a query (system-camera path).
    return {
      kind: 'station',
      locationId: loc,
      path: `/stations/${loc}`,
    };
  }

  // v2 internal-navigation format — `/stations/:loc/stall/:charger`.
  // Preserved for backward compat with any deep links already
  // generated in v2's lifetime. Normalised to the canonical
  // /charging-station form so the URL bar agrees with the sticker.
  const stallMatch = pathname.match(
    /^\/stations\/([A-Za-z0-9][A-Za-z0-9_-]*)\/stall\/([A-Za-z0-9][A-Za-z0-9_-]*)\/?$/,
  );
  if (stallMatch) {
    const loc = stallMatch[1]!;
    const charger = stallMatch[2]!;
    return {
      kind: 'stall',
      locationId: loc,
      chargerIdentity: charger,
      path: `/charging-station/${loc}?charger=${charger}`,
    };
  }

  // Bare `/stations/:loc` — opens StationDetailPage. No canonical
  // v1 equivalent (stickers always carry a charger identity), so
  // leave the path shape as-is.
  const stationMatch = pathname.match(/^\/stations\/([A-Za-z0-9][A-Za-z0-9_-]*)\/?$/);
  if (stationMatch) {
    return {
      kind: 'station',
      locationId: stationMatch[1]!,
      path: `/stations/${stationMatch[1]}`,
    };
  }

  return { kind: 'unknown' };
}
